/**
 * Remontée des erreurs JS du client vers PocketBase (collection `client_logs`).
 *
 * Pas de service externe : on poste en direct sur l'API REST avec `keepalive`,
 * pour que le log parte même si la page se ferme dans la foulée. Toute erreur
 * d'envoi est avalée — un logger qui plante ne doit jamais casser l'app.
 */
import {version} from '../version.js';

const host = import.meta.env.PROD ? window.location.origin : import.meta.env.VITE_APP_CLIENT_POCKETBASE_URL ?? '/pb';
const ENDPOINT = `${host}/api/collections/client_logs/records`;

// Garde-fous : une boucle d'erreurs dans un requestAnimationFrame peut générer
// des milliers de logs identiques par minute.
const MAX_LOGS_PER_SESSION = 100;
const DEDUP_WINDOW_MS = 10000;

let sent = 0;
const recent = new Map(); // clé -> timestamp du dernier envoi

// Identifiant de session : permet de recoller la séquence d'événements d'un
// même scan (breadcrumbs) dans l'admin PocketBase.
const sessionId = Math.random().toString(36).slice(2, 12);

function authToken() {
	try {
		const raw = localStorage.getItem('pocketbase_auth');
		return raw ? (JSON.parse(raw).token || null) : null;
	} catch (e) {
		return null;
	}
}

function currentUserId() {
	try {
		const raw = localStorage.getItem('pocketbase_auth');
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return (parsed.model && parsed.model.id) || null;
	} catch (e) {
		return null;
	}
}

function truncate(value, max) {
	if (value === null || value === undefined) return null;
	const s = String(value);
	return s.length > max ? s.slice(0, max) : s;
}

/** Contexte appareil : c'est lui qui dira pourquoi ça plante sur ce téléphone-là. */
function deviceContext() {
	const n = navigator;
	return {
		screen: `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`,
		viewport: `${window.innerWidth}x${window.innerHeight}`,
		platform: n.userAgentData ? n.userAgentData.platform : n.platform,
		memory_gb: n.deviceMemory ?? null,
		cores: n.hardwareConcurrency ?? null,
		connection: n.connection ? n.connection.effectiveType : null,
		cv_ready: typeof window.cv !== 'undefined' && typeof window.cv.imread === 'function',
		online: n.onLine,
	};
}

function post(payload) {
	const headers = {'Content-Type': 'application/json'};
	const token = authToken();
	if (token) headers['Authorization'] = token;

	try {
		fetch(ENDPOINT, {
			method: 'POST',
			headers,
			body: JSON.stringify(payload),
			keepalive: true,
		}).catch(() => {
		});
	} catch (e) {
		/* logger muet */
	}
}

/**
 * @param {'error'|'warn'|'info'} level
 * @param {string} source  d'où vient le log (ex. 'capture.extract')
 * @param {string|Error} message
 * @param {object} context  données libres (dimensions, état, etc.)
 */
export function log(level, source, message, context = {}) {
	if (sent >= MAX_LOGS_PER_SESSION) return;

	const err = message instanceof Error ? message : null;
	const text = err ? `${err.name}: ${err.message}` : String(message);

	const key = `${level}|${source}|${text}`;
	const now = Date.now();
	const last = recent.get(key);
	if (last && now - last < DEDUP_WINDOW_MS) return;
	recent.set(key, now);
	sent++;

	post({
		level,
		source: truncate(source, 200),
		message: truncate(text, 2000),
		stack: truncate(err ? err.stack : (context && context.stack) || null, 8000),
		url: truncate(window.location.href, 500),
		user: truncate(currentUserId(), 50),
		user_agent: truncate(navigator.userAgent, 500),
		app_version: truncate(version, 50),
		session: sessionId,
		context: {...deviceContext(), ...context},
	});
}

export const logError = (source, error, context) => log('error', source, error, context);
export const logInfo = (source, message, context) => log('info', source, message, context);

/** Branche les handlers globaux. À appeler une seule fois, depuis main.js. */
export function installLogger(app) {
	app.config.errorHandler = (err, instance, info) => {
		logError('vue', err, {hook: info, component: instance && instance.$options && instance.$options.__name});
		console.error(err);
	};

	window.addEventListener('error', (event) => {
		// Erreur de chargement de ressource (script/img) : pas d'objet Error.
		if (event.error) {
			logError('window.error', event.error, {file: event.filename, line: event.lineno, col: event.colno});
		} else if (event.target && event.target.src) {
			logError('resource', `Échec de chargement: ${event.target.src}`, {tag: event.target.tagName});
		}
	}, true);

	window.addEventListener('unhandledrejection', (event) => {
		const reason = event.reason;
		const text = reason instanceof Error ? reason.message : String(reason);

		// Le SDK PocketBase annule lui-même ses requêtes en double ou en cours
		// lors d'un changement de page : attendu, ce n'est pas une panne.
		// Une vraie erreur réseau (status 0 sans ce message) reste remontée.
		if (text && text.indexOf('autocancelled') !== -1) return;

		logError('unhandledrejection', reason instanceof Error ? reason : text, {});
	});

	logInfo('app', 'session start', {});
}
